import { createIcon, IPrimitiveIcon, PrimitiveIcon, Svg } from '@gluestack-ui/core/icon/creator'
import { cssInterop } from 'nativewind'
import React from 'react'
import { Platform } from 'react-native'

import { splitWebStyle } from './webStyle'

// Local replacement for @gluestack-ui/core's PrimitiveIcon. Upstream forwards
// the className colour (classNameColor) to the `as` component only as `stroke`,
// and only when stroke === 'currentColor'. Our line icons are fill-based and our
// Icon wrapper forces stroke='transparent', which drops the colour entirely — so
// `text-*` tinting never reached the SVG. This version also forwards `color`,
// letting `fill='currentColor'` paths resolve to the className colour.
const ColorForwardingPrimitiveIcon = React.forwardRef<
    React.ComponentRef<typeof Svg>,
    IPrimitiveIcon & { style?: React.ComponentPropsWithoutRef<typeof Svg>['style'] }
>(function ColorForwardingPrimitiveIcon(
    {
        height,
        width,
        fill,
        color,
        classNameColor,
        size,
        stroke = 'currentColor',
        as: AsComp,
        style,
        ...props
    },
    ref,
) {
    const resolvedColor = color ?? classNameColor
    const sizeProps = size ? { size } : { height, width }
    const colorProps: { fill?: typeof fill; stroke?: typeof stroke; color?: string } = {}
    if (fill) {
        colorProps.fill = fill
    }
    if (stroke !== 'currentColor') {
        colorProps.stroke = stroke
    } else if (resolvedColor !== undefined) {
        colorProps.stroke = resolvedColor
    }
    if (resolvedColor !== undefined) {
        colorProps.color = resolvedColor
    }
    if (AsComp) {
        const styleProps =
            Platform.OS === 'web'
                ? splitWebStyle(style, (props as { className?: unknown }).className)
                : { style }
        return <AsComp ref={ref} {...props} {...styleProps} {...sizeProps} {...colorProps} />
    }
    return <Svg ref={ref} height={height} width={width} {...colorProps} {...props} />
})

export const UIIcon = createIcon({
    Root: ColorForwardingPrimitiveIcon,
}) as React.ForwardRefExoticComponent<
    React.ComponentPropsWithoutRef<typeof PrimitiveIcon> &
        React.RefAttributes<React.ComponentRef<typeof Svg>>
>

cssInterop(UIIcon, {
    className: {
        target: 'style',
        nativeStyleToProp: {
            height: true,
            width: true,
            fill: true,
            color: 'classNameColor',
            stroke: true,
        },
    },
})
